import React from 'react';

interface PaginationInfo {
  count: number;
  next: string | null;
  previous: string | null;
}

interface TablePaginationProps {
  currentPage: number;
  onPageChange: (page: number) => void;
  totalItems: number;
  itemsPerPage: number;
  paginationInfo: PaginationInfo;
  itemName?: string;
  showText?: boolean;
  nextIcon?: React.ReactNode;
  previousIcon?: React.ReactNode;
}

export const TablePagination: React.FC<TablePaginationProps> = ({
  currentPage,
  onPageChange,
  totalItems,
  itemsPerPage,
  paginationInfo,
  itemName = 'items',
  showText = true,
  nextIcon,
  previousIcon
}) => {
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className={`px-6 py-4 border-t border-gray-800 flex items-center ${showText ? 'justify-between' : 'justify-center'} bg-[#0A0F1C]`}>
      {showText && (
        <div className="text-sm text-gray-400">
          Showing <span className="font-medium">{startIndex}</span> to{' '}
          <span className="font-medium">{endIndex}</span> of{' '}
          <span className="font-medium">{totalItems}</span> {itemName}
        </div>
      )}
      <div className="flex items-center space-x-2">
        <button
          onClick={() => paginationInfo.previous && onPageChange(currentPage - 1)}
          disabled={!paginationInfo.previous}
          className="px-3 py-1 rounded-lg bg-gray-800/50 text-gray-300 hover:bg-gray-800/80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {previousIcon || 'Previous'}
        </button>
        {totalItems > 0 && (
          <div className="flex items-center space-x-2">
            {currentPage > 2 && (
              <button
                onClick={() => onPageChange(1)}
                className="px-3 py-1 rounded-lg bg-gray-800/50 text-gray-300 hover:bg-gray-800/80 transition-colors"
              >
                1
              </button>
            )}
            {currentPage > 3 && (
              <span className="text-gray-500">...</span>
            )}
            {currentPage > 1 && (
              <button
                onClick={() => onPageChange(currentPage - 1)}
                className="px-3 py-1 rounded-lg bg-gray-800/50 text-gray-300 hover:bg-gray-800/80 transition-colors"
              >
                {currentPage - 1}
              </button>
            )}
            <span className="px-3 py-1 rounded-lg bg-blue-500/10 text-blue-500">
              {currentPage}
            </span>
            {currentPage < Math.ceil(totalItems / itemsPerPage) && (
              <button
                onClick={() => onPageChange(currentPage + 1)}
                className="px-3 py-1 rounded-lg bg-gray-800/50 text-gray-300 hover:bg-gray-800/80 transition-colors"
              >
                {currentPage + 1}
              </button>
            )}
            {currentPage < Math.ceil(totalItems / itemsPerPage) - 2 && (
              <span className="text-gray-500">...</span>
            )}
            {currentPage < Math.ceil(totalItems / itemsPerPage) - 1 && (
              <button
                onClick={() => onPageChange(Math.ceil(totalItems / itemsPerPage))}
                className="px-3 py-1 rounded-lg bg-gray-800/50 text-gray-300 hover:bg-gray-800/80 transition-colors"
              >
                {Math.ceil(totalItems / itemsPerPage)}
              </button>
            )}
          </div>
        )}
        <button
          onClick={() => paginationInfo.next && onPageChange(currentPage + 1)}
          disabled={!paginationInfo.next}
          className="px-3 py-1 rounded-lg bg-gray-800/50 text-gray-300 hover:bg-gray-800/80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {nextIcon || 'Next'}
        </button>
      </div>
    </div>
  );
};
